/**
 * SEO Configuration and Utilities
 * Provides route-specific meta tags and Open Graph data
 */

import { ASSET_VERSIONS, getVersionedAsset } from './constants';
import { apps } from './dataModel';

const BASE_URL = process.env.REACT_APP_SITE_URL || 'https://alekhagopian.com';
export const SITE_NAME = 'Alek Hagopian';
const CONTACT_EMAIL = 'alek@solidly.ai';

// Default meta tags for the site
export const defaultMeta = {
  title: 'Alek Hagopian — AI & Data Engineer',
  description: 'I build data and AI systems that actually ship. A decade in data, eight years in AI, one AI company founded and sold. Available for client projects, and the maker of Solidly Finance and Solidly Meet.',
  keywords: 'Alek Hagopian, AI engineer, AI consultant, data engineer, machine learning consultant, applied AI, data warehouse, AI implementation, independent consultant',
  ogImage: `${BASE_URL}${getVersionedAsset('/og-image.jpg', ASSET_VERSIONS.OG_IMAGE)}`,
  twitterImage: `${BASE_URL}${getVersionedAsset('/twitter-image.jpg', ASSET_VERSIONS.TWITTER_IMAGE)}`,
};

// Route-specific SEO data
export const routeSEO = {
  '/': {
    title: 'Alek Hagopian — AI & Data Engineer',
    description: 'I help companies stop talking about AI and start building. A decade in data, eight years in AI, one company founded and sold. Available for projects — and I build my own apps.',
    keywords: defaultMeta.keywords,
    canonical: BASE_URL,
  },
  '/work': {
    title: 'Client Work | Alek Hagopian',
    description: 'Engagements delivered for Crain Communications, Hart Energy, Mastercard, Insurity, Amplify Data and others — data warehouses, neural networks, and AI products taken to production.',
    keywords: 'AI case studies, data engineering projects, machine learning consulting portfolio, AI implementation examples',
    canonical: `${BASE_URL}/work`,
  },
  '/apps': {
    title: "Apps I've Built | Alek Hagopian",
    description: 'Solidly Finance, a private offline financial planner, and Solidly Meet, a desktop app that brings Claude into your meetings live. Both run on your own machine and are bought once, not subscribed to.',
    keywords: 'Solidly Finance, Solidly Meet, financial planning app, private finance software, AI meeting assistant, indie software',
    canonical: `${BASE_URL}/apps`,
  },
  '/about': {
    title: 'About Alek Hagopian',
    description: "I'm Alek Hagopian — I built and sold an AI company, consulted at Mastercard, and now build production data and AI systems independently. Here's how I work.",
    keywords: 'Alek Hagopian, AI founder, Mastercard consultant, AI engineer background, how I work',
    canonical: `${BASE_URL}/about`,
  },
};

/**
 * Get SEO metadata for a specific route
 * @param {string} pathname - The current route pathname
 * @returns {object} SEO metadata object
 */
export const getSEOForRoute = (pathname) => {
  const routeData = routeSEO[pathname] || routeSEO['/'];

  return {
    title: routeData.title,
    description: routeData.description,
    keywords: routeData.keywords || defaultMeta.keywords,
    canonical: routeData.canonical,
    ogTitle: routeData.ogTitle || routeData.title,
    ogDescription: routeData.ogDescription || routeData.description,
    ogImage: routeData.ogImage || defaultMeta.ogImage,
    twitterImage: routeData.twitterImage || defaultMeta.twitterImage,
  };
};

/**
 * Structured data describing Alek himself. This is the primary entity for the site —
 * it replaced an Organization schema when the site stopped being a company site.
 * @returns {object} JSON-LD Person schema
 */
export const getPersonSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${BASE_URL}#alek-hagopian`,
  name: 'Alek Hagopian',
  url: BASE_URL,
  image: `${BASE_URL}/alek_hagopian.jpg`,
  jobTitle: 'AI & Data Engineer',
  description: 'Independent AI and data engineer. Founded and sold an AI company, former Senior Managing Consultant at Mastercard, and the maker of Solidly Finance and Solidly Meet.',
  email: CONTACT_EMAIL,
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'Virginia',
    addressCountry: 'US'
  },
  knowsAbout: [
    'Applied Artificial Intelligence',
    'Machine Learning',
    'Neural Networks',
    'Data Engineering',
    'Data Warehouse Architecture',
    'Analytics Engineering',
    'AI Product Development'
  ],
  worksFor: {
    '@type': 'Organization',
    name: 'Independent'
  }
});

/**
 * Structured data for the two apps Alek distributes, so they can surface as software
 * results rather than only as page text.
 * @returns {object} JSON-LD ItemList of SoftwareApplication entries
 */
export const getSoftwareApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Apps built by Alek Hagopian',
  itemListElement: apps.map((app, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'SoftwareApplication',
      name: app.name,
      description: app.description,
      applicationCategory: app.id === 'solidly-finance' ? 'FinanceApplication' : 'BusinessApplication',
      // Only platforms that actually ship today — a display label like "macOS soon"
      // is fine for a human but is not a valid operatingSystem value.
      operatingSystem: app.platforms
        .filter(platform => !/soon/i.test(platform))
        .join(', '),
      url: app.cta.url,
      author: {
        '@type': 'Person',
        name: 'Alek Hagopian',
        '@id': `${BASE_URL}#alek-hagopian`
      }
    }
  }))
});

/**
 * Generate FAQ structured data for rich search results
 * @returns {object} JSON-LD FAQ schema
 */
export const getFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Who is Alek Hagopian?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Alek Hagopian is an independent AI and data engineer with 8+ years in AI and 10+ years in data. He built his first AI company in 2018 and sold it in 2021, and was previously a Senior Managing Consultant at Mastercard.'
      }
    },
    {
      '@type': 'Question',
      name: 'What kind of projects does Alek take on?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Three kinds: data and analytics engineering (warehouses, pipelines, governance), applied AI and machine learning (predictive models, neural networks, AI agents), and productization (taking a working model and shipping it as software people use).'
      }
    },
    {
      '@type': 'Question',
      name: 'Does he work with startups or large companies?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both. Past clients include Mastercard, Hart Energy, Crain Communications, and Insurity, alongside seed-stage startups building their first AI product.'
      }
    },
    {
      '@type': 'Question',
      name: 'What software has Alek built?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Solidly Finance, a desktop financial planning app that keeps all of your financial data on your own machine, and Solidly Meet, a desktop app that brings Claude into your meetings live without a bot joining the call. Both are one-time purchases rather than subscriptions.'
      }
    },
    {
      '@type': 'Question',
      name: 'How do I get in touch?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Use the contact form on the site or email ${CONTACT_EMAIL}. The first conversation is free — describe the problem and you will get a straight answer about how it would be approached.`
      }
    }
  ]
});
